import React, { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { REGISTRATION_OPEN } from "@/config";

// Countdown to 16 July 2026 (Last Registration Date), end of day Dhaka time
const TARGET = new Date("2026-07-16T23:59:59+06:00").getTime();

function useCountdown(target) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const diff = Math.max(target - now, 0);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff / 3600000) % 24);
    const minutes = Math.floor((diff / 60000) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { days, hours, minutes, seconds, expired: diff <= 0 };
}

const Cell = ({ value, label, testid }) => (
    <div className="flex flex-col items-center min-w-[74px] md:min-w-[110px]">
        <div
            className="relative w-full rounded-2xl bg-inkbrown-900/85 border border-vgold-400/40 px-3 py-4 md:px-5 md:py-6 text-center shadow-goldring"
            data-testid={testid}
        >
            <span className="font-title text-3xl md:text-5xl font-bold text-vgold-200 tabular-nums tracking-tight">
                {String(value).padStart(2, "0")}
            </span>
            <span className="pointer-events-none absolute inset-x-3 top-1/2 h-px bg-vgold-400/20" />
        </div>
        <span className="mt-2 font-body text-[10px] md:text-xs uppercase tracking-[0.25em] text-inkbrown-500">
            {label}
        </span>
    </div>
);

export default function CountdownBanner() {
    const { days, hours, minutes, seconds, expired } = useCountdown(TARGET);
    return (
        <section
            id="countdown"
            className="relative -mt-24 md:-mt-28 z-20 px-4 md:px-12 lg:px-24"
            data-testid="countdown-section"
        >
            <div className="mx-auto max-w-5xl glass-gold rounded-3xl px-6 py-6 md:px-10 md:py-8 shadow-divine paisley-border">
                {!REGISTRATION_OPEN ? (
                    <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 justify-between">
                        <div className="text-center md:text-left flex-1">
                            <p className="font-body text-xs uppercase tracking-[0.3em] text-maroon-600 font-semibold">
                                Registration Status
                            </p>
                            <p className="font-heading text-2xl md:text-3xl text-inkbrown-900 mt-1">
                                Registrations are now closed
                            </p>
                            <p className="font-body text-sm text-inkbrown-500 mt-1">
                                Every seat is reserved. See you on 17 July 2026 at Nitai Gaur Temple.
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="grid place-items-center w-16 h-16 rounded-full bg-vgold-50 border border-vgold-300">
                                <Lock className="w-7 h-7 text-vgold-700" />
                            </span>
                            <div className="text-left">
                                <p className="font-body text-[10px] uppercase tracking-[0.3em] text-inkbrown-500">Event Day</p>
                                <p className="font-title text-2xl md:text-3xl text-inkbrown-900">17 · 07 · 2026</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 justify-between">
                        <div className="text-center md:text-left">
                            <p className="font-body text-xs uppercase tracking-[0.3em] text-maroon-600 font-semibold">
                                Last Date for Registration
                            </p>
                            <p className="font-heading text-2xl md:text-3xl text-inkbrown-900 mt-1">
                                16 July 2026
                            </p>
                            <p className="font-body text-sm text-inkbrown-500 mt-1">
                                {expired
                                    ? "Registration window has closed."
                                    : "Secure your seat before the doors of grace close."}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4">
                            <Cell value={days} label="Days" testid="countdown-days" />
                            <span className="font-title text-3xl text-vgold-400">:</span>
                            <Cell value={hours} label="Hours" testid="countdown-hours" />
                            <span className="font-title text-3xl text-vgold-400">:</span>
                            <Cell value={minutes} label="Minutes" testid="countdown-minutes" />
                            <span className="font-title text-3xl text-vgold-400">:</span>
                            <Cell value={seconds} label="Seconds" testid="countdown-seconds" />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
